import { z } from 'zod';
import { OrgRef, Uuid } from './common.js';
import { MembershipLevel } from './membership.js';

/**
 * An org the caller belongs to, **plus what the membership row itself says about
 * them**: the person on that org's graph this caller *is* (import note §1.14),
 * and what they may do there (privilege-levels note §7).
 *
 * Null for a membership nobody has matched to a person, which is every one of
 * them until an import's preview or the admin member list sets it. It rides here
 * rather than being asked for per org because it is one column of a row `/me`
 * already reads, and because the SPA wants it before it routes: "your own
 * record" is a destination, not a detail of a page.
 *
 * It is deliberately **not** how the record head decides to say "This is you" —
 * that reads `linked_user` off the record itself and compares user ids, because
 * the record is the thing that knows who it is linked to and the comparison must
 * hold for a colleague's link as well as the caller's own.
 */
export const MeOrgRef = OrgRef.extend({
  person_entity_id: Uuid.nullable(),
  /**
   * **What this reader may do in this organization** (privilege-levels note §7).
   *
   * It rides here for the same reason `person_entity_id` does: it is one column
   * of a row `/me` already reads, and the SPA wants it before it routes — a
   * member who cannot start research should be shown a page without the button,
   * not a button that apologizes when they press it.
   *
   * **It is not the fence.** `WriteAccessGuard` is. What this field buys is a
   * coherent app rather than a wall of refusals.
   */
  level: MembershipLevel,
});
export type MeOrgRef = z.infer<typeof MeOrgRef>;

/**
 * `GET /me` — the org-less "who am I" read (decided 2026-08-26). It answers the
 * nav foot's name, and it is the bootstrap read: `orgs[]` is what `GET /orgs`
 * returns, so the SPA needs one call before routing.
 *
 * `:orgId` in every dashboard path is the org **uuid**; `slug` is display-only.
 * An empty `orgs[]` is the legitimate first-login state for an operator account
 * (Auth0 `admin` permission, no membership row) — render the empty state, never
 * a redirect loop.
 */
export const MeResponse = z.object({
  user: z.object({
    id: Uuid,
    name: z.string().nullable(),
    email: z.email(),
  }),
  orgs: z.array(MeOrgRef),
});
export type MeResponse = z.infer<typeof MeResponse>;

/** `GET /orgs` — the caller's orgs, same rows as `GET /me`'s `orgs[]`. */
export const OrgListResponse = z.object({
  orgs: z.array(MeOrgRef),
});
export type OrgListResponse = z.infer<typeof OrgListResponse>;
